import './styles.css';
import { AlertSound } from './audio';
import { clampInteger } from './format';
import { DocumentPipManager, supportsDocumentPip } from './pip';
import { StorageService } from './storage';
import { Timer } from './timer';
import { renderMainView, updateMainView } from './ui';

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('找不到應用程式根節點。');

const timer = new Timer();
const storage = new StorageService();
const sound = new AlertSound();
const settings = storage.loadSettings();
const savedTimer = storage.loadTimer();
if (savedTimer && savedTimer.phase !== 'idle') timer.restore(savedTimer);

const elements = renderMainView(root, settings, supportsDocumentPip());
let zeroWasReached = timer.snapshot().signedRemainingMs <= 0 && timer.snapshot().phase !== 'idle';
let lastPersistAt = 0;
let lastPhase = timer.snapshot().phase;

const pip = new DocumentPipManager({
  getSnapshot: () => timer.snapshot(),
  getSettings: () => settings,
  actions: {
    togglePause,
    addMinute: () => addTime(60_000),
    addFiveMinutes: () => addTime(300_000),
    reset,
  },
  onWindowStateChange: () => render(),
  onSizeChange: (width, height) => {
    settings.pipWidth = width;
    settings.pipHeight = height;
    storage.saveSettings(settings);
  },
});

elements.start.addEventListener('click', () => {
  const durationMs = readDuration();
  if (durationMs === null) return;

  // requestWindow() is invoked synchronously inside this click event. Do not
  // move it behind an await, timeout, or unrelated asynchronous operation.
  const pipRequest = pip.open();
  timer.start(durationMs);
  zeroWasReached = false;
  persistTimer();
  render();
  void pipRequest.then((opened) => {
    if (!opened && pip.isSupported) {
      elements.pipMessage.textContent = '置頂視窗未能開啟。請允許彈出視窗後，按「重新開啟置頂視窗」。計時仍在主頁繼續。';
      elements.pipMessage.classList.add('is-warning');
    }
    render();
  });
});

elements.pause.addEventListener('click', togglePause);
elements.reset.addEventListener('click', reset);
elements.plusOne.addEventListener('click', () => addTime(60_000));
elements.plusFive.addEventListener('click', () => addTime(300_000));
elements.openPip.addEventListener('click', () => {
  void pip.open().then((opened) => {
    if (!opened) {
      elements.pipMessage.textContent = '無法開啟置頂視窗。請確認使用桌面版 Chrome／Edge、HTTPS 網址，且未封鎖彈出視窗。';
      elements.pipMessage.classList.add('is-warning');
    }
    render();
  });
});

elements.minutes.addEventListener('change', saveInputs);
elements.seconds.addEventListener('change', saveInputs);
elements.sound.addEventListener('change', () => {
  settings.soundEnabled = elements.sound.checked;
  storage.saveSettings(settings);
});
elements.animations.addEventListener('change', () => {
  settings.animationsEnabled = elements.animations.checked;
  storage.saveSettings(settings);
  render();
});

window.addEventListener('keydown', (event) => {
  if ((event.target as HTMLElement | null)?.matches('input, textarea, select, [contenteditable="true"]')) return;
  const key = event.key.toLowerCase();
  if (event.code === 'Space') {
    event.preventDefault();
    togglePause();
  } else if (key === 'r') {
    reset();
  } else if (key === '1') {
    addTime(60_000);
  } else if (key === '5') {
    addTime(300_000);
  } else if (key === 'f') {
    if (pip.isOpen) pip.toggleDisplayMode();
    else void pip.open().then(render);
  } else if (event.key === 'Escape' && pip.isOpen) {
    pip.close();
  }
});

window.addEventListener('beforeunload', persistTimer);
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) render();
  persistTimer();
});

function readDuration(): number | null {
  const minutes = clampInteger(elements.minutes.value, 0, 999);
  const seconds = clampInteger(elements.seconds.value, 0, 59);
  elements.minutes.value = String(minutes);
  elements.seconds.value = String(seconds);
  settings.minutes = minutes;
  settings.seconds = seconds;
  storage.saveSettings(settings);

  const durationMs = (minutes * 60 + seconds) * 1_000;
  if (durationMs < 1_000) {
    elements.inputError.textContent = '請設定至少 1 秒的計時長度。';
    elements.minutes.focus();
    return null;
  }
  elements.inputError.textContent = '';
  return durationMs;
}

function saveInputs(): void {
  settings.minutes = clampInteger(elements.minutes.value, 0, 999);
  settings.seconds = clampInteger(elements.seconds.value, 0, 59);
  elements.minutes.value = String(settings.minutes);
  elements.seconds.value = String(settings.seconds);
  elements.inputError.textContent = '';
  storage.saveSettings(settings);
  if (timer.snapshot().phase === 'idle') renderIdleTime();
}

function togglePause(): void {
  timer.togglePause();
  persistTimer();
  render();
}

function addTime(milliseconds: number): void {
  if (timer.snapshot().phase === 'idle') {
    const totalSeconds = Math.min(999 * 60 + 59, settings.minutes * 60 + settings.seconds + milliseconds / 1_000);
    settings.minutes = Math.floor(totalSeconds / 60);
    settings.seconds = totalSeconds % 60;
    elements.minutes.value = String(settings.minutes);
    elements.seconds.value = String(settings.seconds);
    storage.saveSettings(settings);
    renderIdleTime();
    return;
  }

  timer.addTime(milliseconds);
  if (timer.snapshot().signedRemainingMs > 0) zeroWasReached = false;
  persistTimer();
  render();
}

function reset(): void {
  timer.reset();
  storage.clearTimer();
  zeroWasReached = false;
  pip.close();
  render();
  renderIdleTime();
}

function persistTimer(): void {
  storage.saveTimer(timer.serialize());
  lastPersistAt = performance.now();
}

function renderIdleTime(): void {
  if (timer.snapshot().phase !== 'idle') return;
  elements.timer.textContent = `${String(settings.minutes).padStart(2, '0')}:${String(settings.seconds).padStart(2, '0')}`;
  elements.timer.dataset.tone = 'green';
}

function render(): void {
  const snapshot = timer.snapshot();
  updateMainView(elements, snapshot, settings, pip.isOpen);
  pip.update(snapshot);

  if (snapshot.phase === 'idle') renderIdleTime();
  if (!zeroWasReached && snapshot.phase !== 'idle' && snapshot.signedRemainingMs <= 0) {
    zeroWasReached = true;
    if (settings.soundEnabled) sound.play();
  }
  if (lastPhase !== snapshot.phase) {
    lastPhase = snapshot.phase;
    persistTimer();
  } else if (snapshot.phase === 'running' && performance.now() - lastPersistAt >= 1_000) {
    persistTimer();
  }
}

function animationLoop(): void {
  render();
  window.requestAnimationFrame(animationLoop);
}

render();
window.requestAnimationFrame(animationLoop);
